import { AiNodeQuota, BasicModelCard } from "src/aiNode/types";
import { ModelProvider } from "src/clients/agent-management";
import { PROVIDER_ICON_BASE_URL } from "src/genai/ModelSelector/constants";
import {
  hostingForModel,
  lifecycleFor,
  regionFromRouting,
  rerouteTargetIdFor,
  successorIdFor,
} from "src/genai/ModelSelector/facets";
import {
  withByokPricing,
  withTpmQuota,
} from "src/genai/ModelSelector/modelOptions";
import { ModelOption } from "src/genai/ModelSelector/types";

const displayNameForCard = (card: BasicModelCard): string =>
  card.display_name ?? card.name;

/** Per-model tokens-per-minute quota, keyed by model id. */
export const tpmByModelIdFromQuota = (
  quotas: AiNodeQuota,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(quotas.usage ?? {}).map(([modelId, usage]) => [
      modelId,
      usage.tokens_per_minute_limit,
    ]),
  );

/**
 * Adapt the backend model cards (+ provided quotas) into the
 * {@link ModelOption} view-model.
 */
export const aiNodeModelsToOptions = (
  cards: BasicModelCard[],
  quotas: AiNodeQuota,
): ModelOption[] => {
  const options = cards.map((card): ModelOption => {
    const provider = (card.providers?.[0] ?? "bedrock") as ModelProvider;
    const successorId = successorIdFor(card);
    const successorCard = successorId
      ? cards.find((candidate) => candidate.model_id === successorId)
      : undefined;

    return {
      id: card.model_id,
      displayName: displayNameForCard(card),
      provider,
      iconUrl: PROVIDER_ICON_BASE_URL + card.icon_path,
      lifecycle: lifecycleFor(card),
      sunsetDate: card.sunset_date ?? undefined,
      hosting: hostingForModel({ provider }),
      region: regionFromRouting(card.routing_target_region),
      intelligence: card.intelligence,
      speed: card.speed,
      inputCostPer1MTokens: card.input_cost_per_1m_tokens,
      outputCostPer1MTokens: card.output_cost_per_1m_tokens,
      // Merged from `quotas` by withTpmQuota below.
      tokensPerMinuteQuota: null,
      contextWindowTokens: card.context_window_size,
      releaseDate: card.release_date,
      successorId,
      successorName: successorCard
        ? displayNameForCard(successorCard)
        : undefined,
      rerouteTargetId: rerouteTargetIdFor(card),
    };
  });

  return withByokPricing(withTpmQuota(options, tpmByModelIdFromQuota(quotas)));
};
